import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../../components/ui/select";
import {
  DEFAULT_TICKET_FILTERS,
  TICKET_STATUS_FILTER_OPTIONS,
  areTicketFiltersEqual,
  cloneTicketFilters } from
"../data/ticketFilters";
import { translateTicketStatus } from "../data/ticketConstants";
import { searchCustomers, searchTechnicians } from "../../../services/fsmService";

function mergeUniqueById(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    if (item?.id == null || item.id === "") return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
}

export function TicketFiltersModal({
  open,
  onOpenChange,
  appliedFilters,
  onApply,
  hideTechnicianFilter = false
}) {
  const [draft, setDraft] = useState(() =>
    cloneTicketFilters(appliedFilters || DEFAULT_TICKET_FILTERS)
  );

  const [customers, setCustomers] = useState([]);
  const [customersPage, setCustomersPage] = useState(0);
  const [customersHasMore, setCustomersHasMore] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLoadingMore, setCustomersLoadingMore] = useState(false);
  const [customersError, setCustomersError] = useState("");
  const customersFetchLockRef = useRef(false);
  const customersRef = useRef([]);
  const selectedCustomerRef = useRef(null);

  const [technicians, setTechnicians] = useState([]);
  const [techniciansLoading, setTechniciansLoading] = useState(false);
  const [techniciansError, setTechniciansError] = useState("");

  useEffect(() => {
    if (open) {
      setDraft(cloneTicketFilters(appliedFilters || DEFAULT_TICKET_FILTERS));
    }
  }, [open, appliedFilters]);

  customersRef.current = customers;

  const loadCustomersPage = useCallback(async (page, { append = false } = {}) => {
    if (customersFetchLockRef.current) return;
    customersFetchLockRef.current = true;

    if (append) setCustomersLoadingMore(true);else
    {
      setCustomersLoading(true);
      setCustomersError("");
    }

    try {
      const result = await searchCustomers({
        page,
        sortBy: "customerId",
        sortDir: "ASC"
      });
      setCustomers((prev) => {
        const next = append ? mergeUniqueById(prev, result.items) : result.items;
        const selected = selectedCustomerRef.current;
        if (
          selected?.id &&
          !next.some((customer) => String(customer.id) === String(selected.id))
        ) {
          return [selected, ...next];
        }
        return next;
      });
      setCustomersPage(result.page);
      setCustomersHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error("Failed to load customers for filters:", err);
      if (!append) setCustomers([]);
      setCustomersError(
        err?.status === 401 ?
        "Session expired. Please refresh and try again." :
        err?.message || "Failed to load customers."
      );
    } finally {
      customersFetchLockRef.current = false;
      setCustomersLoading(false);
      setCustomersLoadingMore(false);
    }
  }, []);

  const handleLoadMoreCustomers = useCallback(() => {
    if (!customersHasMore || customersLoadingMore || customersLoading) return;
    loadCustomersPage(customersPage + 1, { append: true });
  }, [
    customersHasMore,
    customersLoadingMore,
    customersLoading,
    customersPage,
    loadCustomersPage
  ]);

  const handleCustomersScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      handleLoadMoreCustomers();
    }
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    customersFetchLockRef.current = false;

    const selectedId = appliedFilters?.customer;
    const existing = customersRef.current.find(
      (customer) => String(customer.id) === String(selectedId)
    );
    selectedCustomerRef.current =
      selectedId && selectedId !== "all" ?
      existing || {
        id: String(selectedId),
        name: `Customer #${selectedId}`
      } :
      null;

    loadCustomersPage(0, { append: false });

    if (hideTechnicianFilter) {
      setTechnicians([]);
      setTechniciansLoading(false);
      setTechniciansError("");
      return () => {
        cancelled = true;
      };
    }

    setTechniciansLoading(true);
    setTechniciansError("");
    searchTechnicians({ page: 0, size: 100, sortBy: "first_name", sortDir: "ASC" })
    .then((data) => {
      if (!cancelled) setTechnicians(data);
    })
    .catch((err) => {
      console.error("Failed to load technicians for filters:", err);
      if (!cancelled) {
        setTechnicians([]);
        setTechniciansError(
          err?.status === 401 ?
          "Session expired. Please refresh and try again." :
          err?.message || "Failed to load technicians."
        );
      }
    })
    .finally(() => {
      if (!cancelled) setTechniciansLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [open, appliedFilters?.customer, loadCustomersPage, hideTechnicianFilter]);

  const isUnchanged = useMemo(
    () =>
    areTicketFiltersEqual(draft, appliedFilters || DEFAULT_TICKET_FILTERS),
    [draft, appliedFilters]
  );

  /** Endpoints are single-dimension — selecting one clears the others. */
  const selectExclusive = (key, value) => {
    if (value === "all") {
      setDraft((prev) => ({ ...prev, [key]: "all" }));
      return;
    }
    setDraft({
      customer: key === "customer" ? value : "all",
      technician: key === "technician" ? value : "all",
      status: key === "status" ? value : "all"
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] sm:w-[90vw] md:w-[560px] max-w-[560px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--color-text-border)]/70 shrink-0">
            <DialogTitle className="text-[20px] font-semibold text-foreground tracking-tight">
              <Trans i18nKey="formActions.filterTickets" />
            </DialogTitle>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition">
              <X className="h-5 w-5" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
            <p className="text-xs text-[var(--color-text-secondary)] font-medium leading-relaxed">
              {hideTechnicianFilter ?
              <Trans i18nKey="dateTime.chooseOneFilterAtATimeCustomerStatus" /> :
              <Trans i18nKey="dateTime.chooseOneFilterAtATimeCustomerTechnician" />
              }
            </p>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="common.customer" /></label>
              <Select
                value={draft.customer}
                onValueChange={(value) => selectExclusive("customer", value)}
                disabled={customersLoading && customers.length === 0}>
                
                <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                  <SelectValue
                    placeholder={
                    customersLoading && customers.length === 0 ?
                    "Loading customers..." :
                    "All Customers"
                    } />
                  
                </SelectTrigger>
                <SelectContent
                  viewportClassName="h-auto max-h-60 overflow-y-auto"
                  onViewportScroll={handleCustomersScroll}>
                  <SelectItem value="all"><Trans i18nKey="fsm.allCustomers" /></SelectItem>
                  {customers.map((customer) =>
                  <SelectItem key={customer.id} value={String(customer.id)}>
                      {customer.name}
                    </SelectItem>
                  )}
                  {customersLoadingMore ?
                  <div className="px-2 py-1.5 text-xs text-[var(--color-text-secondary)] text-center">
                      Loading more...
                    </div> :
                  null}
                </SelectContent>
              </Select>
              {customersError ?
              <p className="text-xs text-[var(--color-error)] font-medium">
                  {customersError}
                </p> :
              null}
            </div>

            {!hideTechnicianFilter ?
            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="fsm.technician" /></label>
              <Select
                value={draft.technician}
                onValueChange={(value) => selectExclusive("technician", value)}
                disabled={techniciansLoading}>
                
                <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                  <SelectValue
                    placeholder={
                    techniciansLoading ?
                    "Loading technicians..." :
                    "All Technicians"
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Trans i18nKey="fsm.allTechnicians" /></SelectItem>
                  {technicians.map((tech) =>
                  <SelectItem key={tech.id} value={String(tech.id)}>
                      {tech.name}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {techniciansError ?
              <p className="text-xs text-[var(--color-error)] font-medium">
                  {techniciansError}
                </p> :
              null}
            </div> :
            null}

            <div className="space-y-2">
              <label className="text-sm font-semibold text-foreground"><Trans i18nKey="status.status" /></label>
              <Select
                value={draft.status}
                onValueChange={(value) => selectExclusive("status", value)}>
                
                <SelectTrigger className="h-11 rounded-xl border-[var(--color-text-border)] bg-white text-sm">
                  <SelectValue placeholder={i18n.t("fsm.allOpenTickets")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><Trans i18nKey="fsm.allOpenTickets" /></SelectItem>
                  {TICKET_STATUS_FILTER_OPTIONS.map((opt) =>
                  <SelectItem key={opt.value} value={opt.value}>
                      {translateTicketStatus(opt.value, i18n.t.bind(i18n))}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-[var(--color-text-border)]/70 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDraft(cloneTicketFilters(DEFAULT_TICKET_FILTERS))}
              className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] bg-white text-sm font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40">
              <Trans i18nKey="formActions.clearFilters" />
            
            </Button>
            <Button
              type="button"
              disabled={isUnchanged}
              onClick={() => {
                const next = cloneTicketFilters(draft);
                if (hideTechnicianFilter) next.technician = "all";
                onApply(next);
                onOpenChange(false);
              }}
              className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-accent)] transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] disabled:opacity-50">
              <Trans i18nKey="formActions.applyFilters" />
            
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}
